import { Box, Checkbox, Flex, Radio, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { FilterHeader } from 'src/common/components/table/SearchHeader';

export const SELECTION_MODE = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
} as const;

type Props<T> = {
  items: T[];
  selectedItems: T[];
  onSelectItem: (item: T) => void;
  showBorder?: boolean;
  getItemId: (item: T) => string;
  getItemLabel: (item: T) => string;
  selectionMode?: (typeof SELECTION_MODE)[keyof typeof SELECTION_MODE];
  searchTermMatchFunction: (items: T, searchTerm: string) => boolean;
};

export function SelectableList<T>({
  items,
  selectedItems,
  onSelectItem,
  getItemLabel,
  getItemId,
  showBorder = false,
  selectionMode = SELECTION_MODE.MULTIPLE,
  searchTermMatchFunction,
}: Props<T>) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredItems = items.filter(item => {
    if (!searchTerm) return true;
    return searchTermMatchFunction(item, searchTerm);
  });

  const SelectControl = selectionMode === SELECTION_MODE.MULTIPLE ? Checkbox : Radio;

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      <FilterHeader
        direction="row-reverse"
        mb="md"
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        inputProps={{
          maw: undefined,
        }}
      />
      <Stack
        gap="xs"
        p={showBorder ? 'md' : undefined}
        bd={showBorder ? '1px solid var(--mantine-color-gray-1)' : undefined}
        bg={showBorder ? 'white' : undefined}
        style={{
          overflowY: 'scroll',
          scrollbarWidth: 'thin',
          borderRadius: showBorder ? 12 : undefined,
        }}
      >
        {filteredItems.map(item => (
          <Flex
            key={getItemId(item)}
            align="center"
            gap="xs"
            onClick={() => onSelectItem(item)}
            style={{
              cursor: 'default',
            }}
            role={'listitem'}
          >
            <SelectControl
              checked={selectedItems.some(c => getItemId(c) === getItemId(item))}
              aria-label={`Select ${getItemId(item)}`}
              onChange={() => {
                /* left empty on purpose, the click is handled by the parent Flex */
              }}
            />
            <Text fw={500} size="sm" c="gray.10">
              {getItemLabel(item)}
            </Text>
          </Flex>
        ))}
      </Stack>
    </Box>
  );
}
